import {
  User,
  USER_TYPES,
  WorkflowExecutionType,
  FeedbackFormTypeEnum,
  FeedbackSubmissionStatusEnum,
  TaskTypeEnum,
} from "api/api.typing";
import Badge from "components/Badge";
import EmptyState from "components/EmptyState";
import { Button } from "components/ui/button";
import { Tabs, TabsContent, TabsTrigger } from "components/ui/tabs";
import { FileBoxIcon, FileTextIcon, InfoIcon, MessageSquareMoreIcon, ClipboardCheckIcon } from "lucide-react";
import { ClipboardDocumentListIcon } from "@heroicons/react/24/outline";
import { SideBar, SideBarPresence } from "pages/project/common/SideBar";
import { SideBarEntityTitle } from "pages/project/common/SideBarEntityTitle";
import { SideBarTabsList } from "pages/project/common/SideBarTabsList";
import { sideBarTabsTriggerClassName } from "pages/project/common/masterDetailLayout";
import {
  getCatToolName,
  PHRASE_CAT_TOOL_ID,
  TaskFileUploadType,
  TRANSLATE5_CAT_TOOL_ID,
  XTM_CAT_TOOL_ID,
} from "pages/project/common/taskBase.typing";
import { FileListTable } from "pages/project/common/taskBase/list/FileListTable";
import { XtmJobLinksTable } from "pages/project/common/taskBase/list/XtmJobLinksTable";
import { TaskBaseDetail } from "pages/project/common/taskBase/sidebar/TaskBaseDetail";
import TaskBaseFilesDownload from "pages/project/common/taskBase/sidebar/TaskBaseFilesDownload";
import TaskBaseFilesUpload from "pages/project/common/taskBase/sidebar/TaskBaseFilesUpload";
import TaskBaseNotesUpload from "pages/project/common/taskBase/sidebar/TaskBaseNotesUpload";
import TaskInstructions from "pages/project/common/taskBase/sidebar/TaskInstructions";
import { InstructionsTabWarning } from "pages/project/common/taskBase/sidebar/instructions/InstructionsTabWarning";
import { MandatoryInstructionsModal } from "pages/project/common/taskBase/sidebar/instructions/MandatoryInstructionsModal";
import { useMandatoryInstructionAck } from "pages/project/common/taskBase/sidebar/instructions/useMandatoryInstructionAck";
import UploadTaskMaterialModal from "pages/project/task/components/modals/UploadTaskMaterialModal";
import FeedbackModal from "pages/project/task/components/modals/FeedbackModal";
import { DeliveryButton } from "pages/project/task/components/sidebar/DeliveryButton";
import { Task, TaskListType, TaskStatus } from "pages/project/task/task.typing";
import { useDownloadPurchaseOrder, useTask, usePreprocessingDeliveryInfo } from "query/task.query";
import { useUser } from "query/user.query";
import { forwardRef, useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useTaskFileStore } from "store/useTaskFileStore";
import { MemSourceProjectLinksTable } from "../../../common/taskBase/list/MemSourceProjectLinksTable";
import { Translate5ProjectLinksTable } from "../../../common/taskBase/list/Translate5ProjectLinksTable";
import { QaFormSidebar } from "./QaFormSidebar";
import { UserHint } from "./UserHint";
import { XtmsideBar } from "./XtmSegmentSideBar";
import { MemSourceSideBar } from "./MemsourceSegmentSideBar";
import { useTaskXtmQAStore } from "store/useTaskXtmQAStore";
import { TaskXtmQASidebar } from "pages/project/common/taskBase/sidebar/TaskXtmQASidebar";
import { XtmQaReportsTable } from "pages/project/common/taskBase/list/XtmQaReportsTable";
import { useShallow } from "zustand/react/shallow";

interface Props {
  selectedTaskId?: string;
  onClose: () => void;
  onErrorNode: () => void;
  onDeliverClicked: () => void;
  filterType: TaskListType;
}

const TaskSideBar = ({ selectedTaskId, ...props }: Props) => {
  const { t } = useTranslation();
  const { isLoading, selectedTask } = useTask(selectedTaskId);
  const { data: currentUser } = useUser();

  if (!isLoading && selectedTaskId && !selectedTask) {
    // todo remove this when get taskBase by id is ready
    toast.error(
      <div className="ml-2 flex flex-col space-y-1">
        <span className="text-color-primary">{t("task.detail.error.title")}</span>
        <span className="text-color-secondary text-sm">{t("task.detail.error.description")}</span>
      </div>,
      {
        id: "no-task-id",
        className: "bg-color-primary",
      }
    );
    props.onErrorNode();
    return null;
  }

  return (
    <SideBarPresence present={!isLoading && !!selectedTask && !!currentUser}>
      {selectedTask && currentUser ? (
        <TaskSideBarInner key="task-detail-panel" {...props} selectedTask={selectedTask} currentUser={currentUser} />
      ) : null}
    </SideBarPresence>
  );
};

type TaskSideBarInnerProps = Omit<Props, "selectedTaskId"> & { selectedTask: Task; currentUser: User };

type UploadModalState = TaskFileUploadType | "hidden";

const TaskSideBarInner = forwardRef<HTMLDivElement, TaskSideBarInnerProps>(function TaskSideBarInner(
  { selectedTask, currentUser, ...props },
  ref
) {
  const isMemsource =
    selectedTask.catToolId === PHRASE_CAT_TOOL_ID && selectedTask.workflowExecutionType === WorkflowExecutionType.CAT;
  const isXtm =
    selectedTask.catToolId === XTM_CAT_TOOL_ID && selectedTask.workflowExecutionType === WorkflowExecutionType.CAT;
  const isTranslate5 =
    selectedTask.catToolId === TRANSLATE5_CAT_TOOL_ID &&
    selectedTask.workflowExecutionType === WorkflowExecutionType.CAT;

  const { t } = useTranslation();
  const [uploadModalState, setUploadModalState] = useState<UploadModalState>("hidden");
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");

  const isUserSupplier = currentUser.userType === USER_TYPES.supplier;
  // Instructions GET is supplier-only; skip the query for customers (avoids NotSupported toasts).
  const instructionAck = useMandatoryInstructionAck(selectedTask.id, isUserSupplier);

  useEffect(() => {
    setUploadModalState("hidden");
    setIsFeedbackModalOpen(false);
    setActiveTab("overview");
  }, [selectedTask.id]);

  const { downloadPurchaseOrder } = useDownloadPurchaseOrder();

  // Pre-fetch preprocessing info for auxiliary tasks to avoid modal resize delay
  usePreprocessingDeliveryInfo(selectedTask.id, selectedTask.taskTypeEnum === TaskTypeEnum.Auxiliary);

  const downloadState = useTaskFileStore(
    useShallow((fileState) => ({
      shouldDisableAction: fileState.shouldDisableAction,
      requestZipFullDownload: fileState.requestZipFullDownload,
    }))
  );

  const qaState = useTaskXtmQAStore(
    useShallow((qaState) => ({
      shouldDisableAction: qaState.shouldDisableAction,
      requestGenerateQAReport: qaState.requestGenerateQAReport,
    }))
  );

  const isInstructionAckBlocked = isUserSupplier && instructionAck.isBlocked;
  // Only gate while an ack is pending: a gated link renders href="#", which breaks "Copy link".
  const onBeforeCatOpen =
    isUserSupplier && instructionAck.needsAcknowledgement
      ? (open: () => void) => instructionAck.runOrConfirm(open, "cat")
      : undefined;
  const isDownloadDisabled = downloadState.shouldDisableAction(selectedTask.id) || isInstructionAckBlocked;
  const isQAGenerationDisabled = qaState.shouldDisableAction(selectedTask.id);
  const isTaskOngoing = selectedTask.status === TaskStatus.OnGoing;
  const isTaskUpcoming = selectedTask.status === TaskStatus.UpComing;
  const isTaskDelivered = selectedTask.status === TaskStatus.Delivered;
  const canUpload = isTaskOngoing || isTaskDelivered;
  const isCanEditSupplierContact =
    isUserSupplier &&
    isTaskUpcoming &&
    selectedTask.supplier.supplierInternallyManaged &&
    selectedTask.supplier.tasksVisibleForAllContacts;

  // Show feedback tab based on backend computed flag
  // The backend checks:
  // - User is a supplier
  // - Task has a feedback form type
  // - Feedback not yet submitted
  // - Task execution status is Delivered/Finished
  // The tab shows:
  // - "Fill feedback" button if feedbackSubmissionStatus = NotSubmitted (edit mode)
  // - Message "already submitted" if feedbackSubmissionStatus = Submitted/Skipped
  const shouldShowFeedbackTab = selectedTask.shouldShowFeedbackIcon;

  return (
    <SideBar
      ref={ref}
      contentKey={selectedTask.id}
      onClose={props.onClose}
      title={
        <SideBarEntityTitle code={selectedTask.taskCode}>
          <Badge color="goldfish">{t("task.fields.task")}</Badge>
          <Badge>{selectedTask.taskType}</Badge>
          {getCatToolName(selectedTask.catToolId) && (
            <Badge dataTestId="task-type-value">{getCatToolName(selectedTask.catToolId)}</Badge>
          )}
        </SideBarEntityTitle>
      }
    >
      <div className="mb-2 flex flex-col gap-2 sm:flex-row" data-test="delivery-container">
        {isUserSupplier ? (
          <Button
            variant="outline"
            data-test="btn-download-po"
            className="flex w-full flex-nowrap gap-2 text-nowrap"
            onClick={() => downloadPurchaseOrder(selectedTask.id)}
          >
            <FileTextIcon className="size-5 shrink-0" data-icon="FileTextIcon" />
            <span>{t("translation:fileDownload.po")}</span>
          </Button>
        ) : null}
        {isTaskOngoing ? (
          <DeliveryButton
            taskId={selectedTask.id}
            isTaskMemSourceCatTool={isMemsource}
            isXtmCatTool={isXtm}
            shouldDisplayQAForm={selectedTask.shouldDisplayQAForm}
            onDeliverClicked={props.onDeliverClicked}
            isUserSupplier={isUserSupplier}
          />
        ) : null}
      </div>
      {isTaskOngoing ? (
        <div>
          <XtmsideBar isXtmCatTool={isXtm} taskId={selectedTask.id} />
          <MemSourceSideBar isMemSourceCatTool={isMemsource} taskId={selectedTask.id} />
          <QaFormSidebar feedbackFormType={selectedTask.feedbackFormType} />
        </div>
      ) : null}
      <UserHint
        fileTabName={isXtm || isMemsource ? t("translation:task.tabs.files-links") : t("translation:task.tabs.files")}
      />

      <Tabs
        value={activeTab}
        onValueChange={setActiveTab}
        // Controlled tabs: value must not follow Dialog RestoreFocus onto a TabsTrigger.
        activationMode="manual"
        className="-mx-6 mt-4 min-w-0 border-t border-gray-300 dark:border-t-gray-600"
      >
        <SideBarTabsList>
          <TabsTrigger value="overview" data-test="overview-tab" className={sideBarTabsTriggerClassName}>
            <InfoIcon className="mr-2 size-4 shrink-0" />
            {t("translation:task.tabs.general")}
          </TabsTrigger>
          <TabsTrigger value="files" data-test="files-tab" className={sideBarTabsTriggerClassName}>
            <FileBoxIcon className="mr-2 size-4 shrink-0" />
            {isXtm || isMemsource ? t("translation:task.tabs.files-links") : t("translation:task.tabs.files")}
          </TabsTrigger>
          {isXtm && isTaskOngoing && isUserSupplier ? (
            <TabsTrigger value="task-qa" data-test="task-qa-tab" className={sideBarTabsTriggerClassName}>
              <ClipboardCheckIcon className="mr-2 size-4 shrink-0" />
              {t("translation:task.tabs.task-qa")}
            </TabsTrigger>
          ) : null}
          {isUserSupplier ? (
            <TabsTrigger value="instructions" data-test="instructions-tab" className={sideBarTabsTriggerClassName}>
              <MessageSquareMoreIcon className="mr-2 size-4 shrink-0" /> {t("translation:task.tabs.instructions")}
              <InstructionsTabWarning show={instructionAck.hasMandatoryInstructions} />
            </TabsTrigger>
          ) : null}
          {shouldShowFeedbackTab ? (
            <TabsTrigger value="feedback" data-test="feedback-tab" className={sideBarTabsTriggerClassName}>
              <ClipboardDocumentListIcon className="mr-2 size-4 shrink-0" /> {t("translation:task.tabs.feedback")}
            </TabsTrigger>
          ) : null}
        </SideBarTabsList>
        <TabsContent value="overview" className="min-w-0 px-6">
          <TaskBaseDetail
            selectedTaskBase={selectedTask}
            taskBaseStartDateLabel={
              isTaskUpcoming ? t("task-base.detail.estimatedStartDate") : t("task-base.detail.startDate")
            }
            isUserSupplier={isUserSupplier}
            isCanEditSupplierContact={isCanEditSupplierContact}
          />
        </TabsContent>
        <TabsContent value="files" className="min-w-0 px-6 py-3">
          {/* Stack full-width actions: 3-col + text-nowrap was expanding the shell past fixed rem */}
          <div className="flex min-w-0 flex-col gap-2" data-test="task-files-actions">
            <TaskBaseFilesDownload
              selectedTaskBase={selectedTask}
              disableDownload={isDownloadDisabled}
              onRequestZipFullDownload={(request, currentLanguage, hubConnectionId) =>
                downloadState.requestZipFullDownload(request.id, request, currentLanguage, hubConnectionId)
              }
              onBeforeDownload={
                isUserSupplier ? (download) => instructionAck.runOrConfirm(download, "download") : undefined
              }
            />
            <TaskBaseFilesUpload
              canUpload={canUpload}
              onClick={() => setUploadModalState("delivery")}
              taskCode={selectedTask.taskCode}
            />
            <TaskBaseNotesUpload
              canUpload={canUpload}
              onClick={() => setUploadModalState("notes")}
              taskCode={selectedTask.taskCode}
            />
          </div>
          {!isXtm && isTaskUpcoming ? (
            <EmptyState
              title={t("task.detail.delivery-files.title-no-files")}
              subtitle={t("task.detail.delivery-files.title-no-files-detail")}
            />
          ) : null}
          {isXtm && (
            <div className="my-4 px-2">
              <XtmJobLinksTable
                isTaskOngoing={isTaskOngoing}
                taskId={selectedTask.id}
                disabled={isInstructionAckBlocked}
                onBeforeOpen={onBeforeCatOpen}
              />
            </div>
          )}

          {!isTaskUpcoming && (
            <div className="flex flex-col gap-2">
              {isMemsource && (
                <div className="my-4 px-2">
                  <MemSourceProjectLinksTable
                    taskId={selectedTask.id}
                    disabled={isInstructionAckBlocked}
                    onBeforeOpen={onBeforeCatOpen}
                  />
                </div>
              )}
              {isTranslate5 && (
                <div className="my-4 px-2">
                  <Translate5ProjectLinksTable
                    taskId={selectedTask.id}
                    disabled={isInstructionAckBlocked}
                    onBeforeOpen={onBeforeCatOpen}
                  />
                </div>
              )}
              {canUpload && (
                <div className="my-4 px-2">
                  <FileListTable taskId={selectedTask.id} />
                </div>
              )}
            </div>
          )}
        </TabsContent>
        {isXtm && isUserSupplier && isTaskOngoing ? (
          <TabsContent value="task-qa" className="px-6 py-3">
            <TaskXtmQASidebar
              selectedTaskBase={selectedTask}
              disableGeneration={isQAGenerationDisabled}
              onGenerateQAReport={(hubConnectionId) => {
                qaState.requestGenerateQAReport(selectedTask.id, selectedTask, hubConnectionId);
              }}
            />
            <div className="my-4 px-2">
              <XtmQaReportsTable taskId={selectedTask.id} />
            </div>
          </TabsContent>
        ) : null}
        {isUserSupplier ? (
          <TabsContent value="instructions" className="min-w-0 px-6">
            <TaskInstructions taskId={selectedTask.id} />
          </TabsContent>
        ) : null}
        {shouldShowFeedbackTab ? (
          <TabsContent value="feedback" className="px-6 py-3">
            <div className="flex flex-col gap-4">
              <div className="text-sm">
                <div className="mb-2 font-semibold">
                  {selectedTask.feedbackFormType === FeedbackFormTypeEnum.MT && t("translation:task.feedback.mt-title")}
                  {selectedTask.feedbackFormType === FeedbackFormTypeEnum.QA && t("translation:task.feedback.qa-title")}
                  {selectedTask.feedbackFormType === FeedbackFormTypeEnum.STAR &&
                    t("translation:task.feedback.star-title")}
                  {selectedTask.feedbackFormType === FeedbackFormTypeEnum.DTP &&
                    t("translation:task.feedback.dtp-title")}
                </div>
                <div className="text-color-secondary">
                  {selectedTask.feedbackSubmissionStatus !== FeedbackSubmissionStatusEnum.NotSubmitted
                    ? t("translation:task.feedback.already-submitted")
                    : t("translation:task.feedback.fill-description")}
                </div>
              </div>
              {selectedTask.feedbackSubmissionStatus === FeedbackSubmissionStatusEnum.NotSubmitted && (
                <Button variant="outline" data-test="btn-fill-feedback" onClick={() => setIsFeedbackModalOpen(true)}>
                  {selectedTask.feedbackFormType === FeedbackFormTypeEnum.MT &&
                    t("translation:task.feedback.fill-mt-button")}
                  {selectedTask.feedbackFormType === FeedbackFormTypeEnum.QA &&
                    t("translation:task.feedback.fill-qa-button")}
                  {selectedTask.feedbackFormType === FeedbackFormTypeEnum.STAR &&
                    t("translation:task.feedback.fill-star-button")}
                  {selectedTask.feedbackFormType === FeedbackFormTypeEnum.DTP &&
                    t("translation:task.feedback.fill-dtp-button")}
                </Button>
              )}
            </div>
          </TabsContent>
        ) : null}
      </Tabs>
      {/*Important to recreate the modal each time to remove the old state. */}
      {uploadModalState !== "hidden" && (
        <UploadTaskMaterialModal
          onClose={() => setUploadModalState("hidden")}
          fileType={uploadModalState}
          taskId={selectedTask.id}
          portalUploadMode={selectedTask.portalUploadMode}
          catToolId={selectedTask.catToolId}
          taskCode={selectedTask.taskCode}
          canDeliver={
            isTaskOngoing &&
            !isMemsource &&
            selectedTask.taskType !== "QualityControl" &&
            !selectedTask.shouldDisplayQAForm
          }
          isUserSupplier={isUserSupplier}
        />
      )}
      {isFeedbackModalOpen && (
        <FeedbackModal show={true} taskId={selectedTask.id} onClose={() => setIsFeedbackModalOpen(false)} />
      )}
      <MandatoryInstructionsModal
        isShown={instructionAck.confirmationContext != null}
        context={instructionAck.confirmationContext ?? "download"}
        instructions={instructionAck.mandatoryInstructions}
        isConfirming={instructionAck.isConfirming}
        onCancel={instructionAck.cancel}
        onConfirm={instructionAck.confirm}
        onViewAllInstructions={() => setActiveTab("instructions")}
      />
    </SideBar>
  );
});

export default TaskSideBar;
